import { useTranslation } from 'react-i18next';
import { PolicyData } from '../../../context/policy';
import {
	ExposureCharacteristics,
	PerilCharacteristics,
} from '../../../types/policy';
import { useRouteMatch } from '../../../util/useRouteMatch';
import {
	getActiveCharacteristics,
	getEntityNameFrom_DescriptionField,
	isExposureActive,
	isPerilActive,
	sortCharacteristicsByCoverageStartDate,
	sortCharacteristicsByStartDate,
} from '../../../util/util';
import {
	TreeItemData,
	TreeItemInterface,
} from '../../layouts/SidePanel/TreeNode';
import { validatePolicyDetails } from '../fields/validations';
import { PolicyRoutes, POLICY_ROUTES } from './PolicySidebar';

export const IssuedPolicyTree = (
	policyData: PolicyData,
	expandedItems: string[],
	currentErrorObject: any,
	hideInactiveEntities: boolean,
) => {
	const { t } = useTranslation();
	const { policy, productConfig } = policyData;

	const { locator, exposures } = policy;
	const routeMatch = useRouteMatch(
		Object.keys(POLICY_ROUTES).map(
			(key) => POLICY_ROUTES[key as keyof PolicyRoutes],
		),
	);
	const params = routeMatch?.params || {};
	const routeName = routeMatch?.pattern?.path || '';

	const policyConfig = productConfig.policyConfiguration;
	const policyLink = `/policy/${locator}`;
	const detailsLink = `${policyLink}/details`;
	const exposuresLink = `${policyLink}/exposures`;

	const policyDetailErrors =
		!!currentErrorObject?.data?.policyFieldErrors?.policyFieldErrors?.hasErrors;
	const exposureErrors =
		!!currentErrorObject?.data?.policyFieldErrors?.exposureValidationErrors
			?.length;
	const isPolicyValid = validatePolicyDetails(
		policy,
		policyConfig,
		`${Date.now()}`,
	).valid;

	const getItems = () => {
		const isDetailsSelected =
			!!params.policyId &&
			!params.exposureId &&
			(routeName === POLICY_ROUTES.POLICY_DETAILS ||
				routeName === POLICY_ROUTES.AUX_DATA);

		let items: TreeItemData = {
			'Policy Details': {
				id: 'Policy Details',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Policy Details',
					link: detailsLink,
					selected: isDetailsSelected,
					hasError: policyDetailErrors || !isPolicyValid,
				},
			},
		};

		const exposureListItem: TreeItemInterface = {
			id: 'Exposures',
			children: [],
			hasChildren: true,
			isExpanded: expandedItems.includes('Exposures'),
			isChildrenLoading: false,
			data: {
				title: t('exposure.plural.upper'),
				link: exposuresLink,
				selected: routeName === POLICY_ROUTES.EXPOSURES,
				hasError: exposureErrors,
				className: 'exposures',
			},
		};

		// Exposures drawer
		const exposureAndPerilItems = exposures.reduce(
			(exposureItems: TreeItemData, exposure, index: number) => {
				const exposureLink = `${exposuresLink}/${exposure.displayId}`;
				const perilsLink = `${exposuresLink}/${exposure.displayId}/perils`;
				const perils = exposure.perils || [];
				// Perils drawer
				const perilTreeItems = perils.reduce(
					(perilItems: { [itemId: string]: TreeItemInterface }, peril) => {
						const perilLink = `${perilsLink}/${peril.displayId}`;
						const exposureConfig = policyConfig.exposures.find(
							(ex) => ex.name === exposure.name,
						);
						const perilConfig = exposureConfig.perils.find(
							(p) => p.name === peril.name,
						);
						const latestActivePerilCharacteristic =
							sortCharacteristicsByCoverageStartDate(
								getActiveCharacteristics(
									peril.characteristics,
								) as PerilCharacteristics[],
							).pop();
						if (!latestActivePerilCharacteristic) {
							return perilItems;
						}
						const isActive = isPerilActive(
							peril,
							exposure,
							policyData.policyViewTimestamp,
						);
						let perilName =
							getEntityNameFrom_DescriptionField(
								latestActivePerilCharacteristic,
							) || perilConfig.displayName;
						if (!isActive) {
							perilName += ' (Inactive)';
						}
						const perilId = peril.locator;
						const isPerilPage = !!params.perilId;
						const isPerilIdSelected =
							isPerilPage && peril.displayId === params.perilId;

						if (isActive || !hideInactiveEntities) {
							perilItems[perilId] = {
								id: perilId,
								children: [],
								hasChildren: false,
								isExpanded: expandedItems.includes(perilId),
								isChildrenLoading: false,
								data: {
									title: perilName,
									link: perilLink,
									className: `peril-${perilId} peril`,
									selected: isPerilIdSelected,
								},
							};
						}

						return perilItems;
					},
					{},
				);

				// Perils side button
				const perilTreeKeys = Object.keys(perilTreeItems);
				const exposureId = exposure.locator;
				const isExposurePage =
					!!params.exposureId &&
					!params.perilId &&
					routeName !== POLICY_ROUTES.PERILS;
				const isExposureIdSelected =
					isExposurePage && exposure.displayId === params.exposureId;

				const latestActiveExposureCharacteristic =
					sortCharacteristicsByStartDate(
						getActiveCharacteristics(
							exposure.characteristics,
						) as ExposureCharacteristics[],
					).pop();
				const perilExposureConfig = policyConfig.exposures.find(
					(ex) => ex.name === exposure.name,
				);
				const exposureDisplayName = perilExposureConfig.displayName
					? perilExposureConfig.displayName
					: exposure.name;
				let exposureName =
					getEntityNameFrom_DescriptionField(
						latestActiveExposureCharacteristic,
					) || exposureDisplayName;

				const isActiveExposure = isExposureActive(
					exposure,
					policy,
					policyData.policyViewTimestamp,
				);
				if (!isActiveExposure) {
					exposureName += ' (Inactive)';
				}

				if (isActiveExposure || !hideInactiveEntities) {
					const exposureItem: TreeItemInterface = {
						id: exposureId,
						children: [],
						hasChildren: true,
						isExpanded: expandedItems.includes(exposureId),
						isChildrenLoading: false,
						data: {
							title: exposureName,
							link: exposureLink,
							className: `exposure-${exposureId} exposure`,
							selected: isExposureIdSelected,
						},
					};

					const perilItemId = `${exposureId}-Perils`;
					const perilItem: TreeItemInterface = {
						id: perilItemId,
						children: perilTreeKeys,
						hasChildren: !!perils.length,
						isExpanded: expandedItems.includes(perilItemId),
						isChildrenLoading: false,
						data: {
							title: t('peril.plural.upper'),
							link: perilsLink,
							className: 'perils',
							selected:
								exposure.displayId === params.exposureId &&
								routeName === POLICY_ROUTES.PERILS,
						},
					};
					perilTreeItems[perilItemId] = perilItem;
					exposureItem.children.push(perilItemId);

					exposureItems[exposureId] = exposureItem;
					exposureListItem.children.push(exposureId);
				}

				return { ...exposureItems, ...perilTreeItems };
			},
			{},
		);

		items = {
			...items,
			...exposureAndPerilItems,
			Exposures: exposureListItem,
		};

		return items;
	};

	return getItems();
};

export default IssuedPolicyTree;
